import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_ADMIN_ANALYTICS } from '~/services/admin';
import {
  LIST_CONTENT,
  CREATE_CONTENT,
  UPDATE_CONTENT,
  DELETE_CONTENT,
} from '~/services/content';
import { ContentEditorDialog } from './ContentEditorDialog';
import ReferralsDashboard from './ReferralsDashboard';
import { Content, ContentFormData, CreateContentInput } from '~/types/content';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

interface MonthlyData {
  _id: string;
  count: number;
}

interface RecentUser {
  _id: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  status: string;
  email: string;
  profile_name?: string;
}

interface RecentBusiness {
  _id: string;
  created_at: string;
  business_name?: string;
  agent_name: string;
  agent_type: string;
  completed_onboarding: boolean;
  owner_name: string;
  branding?: any;
  business_rules?: any;
  shopify?: any;
}

interface TestNumber {
  _id: string;
  business_name?: string;
  agent_name: string;
  created_at: string;
  test_number: string;
  test_profile_name?: string;
  agent_id: string;
}

interface AgentWithoutChannels {
  _id: string;
  name: string;
  created_at: string;
  manual_mode: boolean;
  adminChannels: number;
  partnerChannels: number;
  business_name?: string;
  business_type?: string;
  owner_name: string;
  completed_onboarding: boolean;
}

interface AnalyticsData {
  monthlyStats: {
    generations: MonthlyData[];
    conversations: MonthlyData[];
    users: MonthlyData[];
    agents: MonthlyData[];
  };
  totals: {
    generations: number;
    conversations: number;
    users: number;
    agents: number;
  };
  detailedData?: {
    recentEmailUsers: RecentUser[];
    recentBusinesses: RecentBusiness[];
    testNumbers: TestNumber[];
    agentsWithoutChannels: AgentWithoutChannels[];
  };
}

const AdminAnalytics: React.FC = () => {
  const { t } = useTranslation('admin');
  const { data, loading, error, refetch } = useQuery(GET_ADMIN_ANALYTICS, {
    errorPolicy: 'all',
  });

  // Content Management State
  const [activeSection, setActiveSection] = useState<
    'analytics' | 'content' | 'referrals'
  >('analytics');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [contentFilter, setContentFilter] = useState({
    type: '',
    status: '',
    searchTerm: '',
  });

  // Content queries and mutations
  const {
    data: contentData,
    loading: contentLoading,
    refetch: refetchContent,
  } = useQuery(LIST_CONTENT, {
    variables: {
      ...contentFilter,
      type: contentFilter.type || undefined,
      status: contentFilter.status || undefined,
      searchTerm: contentFilter.searchTerm || undefined,
      page: 1,
      limit: 20,
    },
    skip: activeSection !== 'content',
  });

  const [createContent, { loading: creating }] = useMutation(CREATE_CONTENT);
  const [updateContent, { loading: updating }] = useMutation(UPDATE_CONTENT);
  const [deleteContent, { loading: deleting }] = useMutation(DELETE_CONTENT);

  // Content Management Functions
  const handleCreateContent = () => {
    setSelectedContent(null);
    setIsEditorOpen(true);
  };

  const handleEditContent = (content: Content) => {
    setSelectedContent(content);
    setIsEditorOpen(true);
  };

  const handleSaveContent = async (formData: ContentFormData) => {
    try {
      // Transform data to match GraphQL schema structure
      const metadata = {
        title: formData.title,
        description: formData.description,
        slug: formData.slug,
        author: formData.author,
        thumbnail: formData.thumbnail,
        tags: formData.tags,
        featured: formData.featured,
      };

      const translations = Object.entries(formData.content).map(
        ([language, content]) => {
          // Generate excerpt from content (first 200 characters, strip HTML)
          const plainText = (content as string).replace(/<[^>]*>/g, '');
          const excerpt =
            plainText.length > 200
              ? plainText.substring(0, 200).trim() + '...'
              : plainText.trim();

          return {
            language,
            content: content as string,
            excerpt,
            isActive: true,
          };
        },
      );

      const seo = formData.seo;

      const input = {
        type: formData.type,
        status: formData.status as 'draft' | 'published',
        metadata: JSON.stringify(metadata),
        translations: JSON.stringify(translations),
        seo: JSON.stringify(seo),
      };

      if (selectedContent) {
        await updateContent({
          variables: { id: selectedContent._id, input },
        });
      } else {
        await createContent({
          variables: { input },
        });
      }

      setIsEditorOpen(false);
      refetchContent();
    } catch (error) {
      console.error('Error saving content:', error);
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        await deleteContent({
          variables: { id: contentId },
        });
        refetchContent();
      } catch (error) {
        console.error('Error deleting content:', error);
      }
    }
  };

  const formatDate = (value: string | Date | null | undefined) => {
    // Graceful handling for missing/placeholder values
    if (!value) return '—';

    // Some backends may literally send the string "Invalid Date"; treat as empty
    if (
      typeof value === 'string' &&
      value.trim().toLowerCase() === 'invalid date'
    ) {
      return '—';
    }

    // Attempt robust parsing
    let date: Date;
    try {
      date = new Date(value as any);
      if (isNaN(date.getTime())) {
        // Try numeric timestamp
        const asNumber = typeof value === 'string' ? Number(value) : NaN;
        date = new Date(asNumber);
      }
    } catch {
      return '—';
    }

    if (isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <div className="alert alert-error mb-4">
          <span>Error loading analytics: {error.message}</span>
        </div>
        <button className="btn btn-primary" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  if (data?.response?.hasError) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="alert alert-error">
          <span>{data.response.message}</span>
        </div>
      </div>
    );
  }

  const analyticsData: AnalyticsData = data?.response?.data;

  if (!analyticsData) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="alert alert-warning">
          <span>No analytics data available</span>
        </div>
      </div>
    );
  }

  // Combine monthly data for chart display
  const combineMonthlyData = () => {
    const months = new Set([
      ...analyticsData.monthlyStats.generations.map((item) => item._id),
      ...analyticsData.monthlyStats.conversations.map((item) => item._id),
      ...analyticsData.monthlyStats.users.map((item) => item._id),
      ...analyticsData.monthlyStats.agents.map((item) => item._id),
    ]);

    return Array.from(months)
      .sort()
      .map((month) => {
        const generationsData = analyticsData.monthlyStats.generations.find(
          (item) => item._id === month,
        );
        const conversationsData = analyticsData.monthlyStats.conversations.find(
          (item) => item._id === month,
        );
        const usersData = analyticsData.monthlyStats.users.find(
          (item) => item._id === month,
        );
        const agentsData = analyticsData.monthlyStats.agents.find(
          (item) => item._id === month,
        );

        return {
          month,
          generations: generationsData?.count || 0,
          conversations: conversationsData?.count || 0,
          users: usersData?.count || 0,
          agents: agentsData?.count || 0,
        };
      });
  };

  const chartData = combineMonthlyData();

  return (
    <div className="mx-auto max-w-7xl p-6">
      {/* Header with Section Tabs */}
      <div className="mb-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-base-content text-3xl font-bold">
              Admin Dashboard
            </h1>
            <p className="text-base-content/70 mt-1">
              Platform analytics and content management
            </p>
          </div>

          {/* Action Button */}
          {activeSection === 'content' && (
            <button
              onClick={handleCreateContent}
              className="btn btn-primary btn-lg gap-2"
              disabled={creating}
            >
              {creating ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Creating...
                </>
              ) : (
                <>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Create Content
                </>
              )}
            </button>
          )}
        </div>

        {/* Section Navigation */}
        <div className="tabs tabs-boxed bg-base-200 p-1">
          <button
            className={`tab tab-lg ${
              activeSection === 'analytics' ? 'tab-active' : ''
            }`}
            onClick={() => setActiveSection('analytics')}
          >
            <span className="mr-2">📊</span>
            Analytics
          </button>
          <button
            className={`tab tab-lg ${
              activeSection === 'content' ? 'tab-active' : ''
            }`}
            onClick={() => setActiveSection('content')}
          >
            <span className="mr-2">📝</span>
            Content Management
          </button>
          <button
            className={`tab tab-lg ${
              activeSection === 'referrals' ? 'tab-active' : ''
            }`}
            onClick={() => setActiveSection('referrals')}
          >
            <span className="mr-2">👥</span>
            Referrals
          </button>
        </div>
      </div>

      {/* Content based on active section */}
      {activeSection === 'analytics' && (
        <div>
          {/* Stats Cards */}
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h3 className="text-base-content/70 text-sm font-medium">
                  Total Users
                </h3>
                <p className="text-primary text-2xl font-bold">
                  {analyticsData.totals.users.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h3 className="text-base-content/70 text-sm font-medium">
                  Total Agents
                </h3>
                <p className="text-secondary text-2xl font-bold">
                  {analyticsData.totals.agents.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h3 className="text-base-content/70 text-sm font-medium">
                  Total Conversations
                </h3>
                <p className="text-accent text-2xl font-bold">
                  {analyticsData.totals.conversations.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h3 className="text-base-content/70 text-sm font-medium">
                  Total Generations
                </h3>
                <p className="text-info text-2xl font-bold">
                  {analyticsData.totals.generations.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Monthly Growth Line Chart */}
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h3 className="card-title text-lg font-semibold">
                  Monthly Growth
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="users"
                        stroke="#8884d8"
                        strokeWidth={2}
                        name="Users"
                      />
                      <Line
                        type="monotone"
                        dataKey="agents"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        name="Agents"
                      />
                      <Line
                        type="monotone"
                        dataKey="conversations"
                        stroke="#ffc658"
                        strokeWidth={2}
                        name="Conversations"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Monthly Activity Bar Chart */}
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h3 className="card-title text-lg font-semibold">
                  Monthly Activity
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="generations"
                        fill="#8884d8"
                        name="Generations"
                      />
                      <Bar
                        dataKey="conversations"
                        fill="#82ca9d"
                        name="Conversations"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* NEW DETAILED DATA TABLES */}
          {analyticsData.detailedData && (
            <>
              {/* Action Required Section */}
              <div className="mb-8 mt-12">
                <h2 className="text-error mb-2 text-2xl font-bold">
                  ⚠️ Admin Action Required
                </h2>
                <p className="text-base-content/70">
                  Review items that may need immediate attention from
                  administrators
                </p>
              </div>

              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* Agents Without Channels */}
                <div className="card bg-base-100 shadow-sm">
                  <div className="card-body">
                    <h3 className="card-title text-warning text-lg font-semibold">
                      🔌 Agents Without Channels (
                      {analyticsData.detailedData.agentsWithoutChannels.length})
                    </h3>
                    <p className="text-base-content/70 mb-4 text-sm">
                      Agents that haven't connected WhatsApp or other messaging
                      channels yet
                    </p>
                    <div className="max-h-96 overflow-x-auto">
                      <table className="table-sm table">
                        <thead>
                          <tr>
                            <th>Agent Name</th>
                            <th>Business</th>
                            <th>Owner</th>
                            <th>Type</th>
                            <th>Created</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.detailedData.agentsWithoutChannels.map(
                            (agent) => (
                              <tr key={agent._id}>
                                <td className="font-medium">{agent.name}</td>
                                <td>{agent.business_name || 'N/A'}</td>
                                <td>{agent.owner_name}</td>
                                <td>
                                  <span className="badge badge-sm badge-outline">
                                    {agent.business_type}
                                  </span>
                                </td>
                                <td>{formatDate(agent.created_at)}</td>
                                <td>
                                  <span
                                    className={`badge badge-sm ${
                                      agent.completed_onboarding
                                        ? 'badge-success'
                                        : 'badge-warning'
                                    }`}
                                  >
                                    {agent.completed_onboarding
                                      ? 'Complete'
                                      : 'Incomplete'}
                                  </span>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Test Numbers */}
                <div className="card bg-base-100 shadow-sm">
                  <div className="card-body">
                    <h3 className="card-title text-info text-lg font-semibold">
                      📱 Test Numbers in Use (
                      {analyticsData.detailedData.testNumbers.length})
                    </h3>
                    <p className="text-base-content/70 mb-4 text-sm">
                      WhatsApp test numbers currently assigned to businesses
                    </p>
                    <div className="max-h-96 overflow-x-auto">
                      <table className="table-sm table">
                        <thead>
                          <tr>
                            <th>Test Number</th>
                            <th>Business</th>
                            <th>Agent</th>
                            <th>Profile Name</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.detailedData.testNumbers.map(
                            (item) => (
                              <tr key={item._id}>
                                <td className="font-mono text-sm font-medium">
                                  {item.test_number}
                                </td>
                                <td>{item.business_name || 'N/A'}</td>
                                <td>{item.agent_name}</td>
                                <td>{item.test_profile_name || 'N/A'}</td>
                                <td>{formatDate(item.created_at)}</td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity Section */}
              <div className="mb-8 mt-12">
                <h2 className="text-primary mb-2 text-2xl font-bold">
                  📊 Recent Activity
                </h2>
                <p className="text-base-content/70">
                  New users and businesses from the last 30 days
                </p>
              </div>

              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* Recent Email Users */}
                <div className="card bg-base-100 shadow-sm">
                  <div className="card-body">
                    <h3 className="card-title text-success text-lg font-semibold">
                      👤 Recent Email Users (
                      {analyticsData.detailedData.recentEmailUsers.length})
                    </h3>
                    <p className="text-base-content/70 mb-4 text-sm">
                      Users who created accounts with email in the last 30 days
                    </p>
                    <div className="max-h-96 overflow-x-auto">
                      <table className="table-sm table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.detailedData.recentEmailUsers.map(
                            (user) => (
                              <tr key={user._id}>
                                <td>
                                  {user.first_name && user.last_name
                                    ? `${user.first_name} ${user.last_name}`
                                    : user.profile_name || 'N/A'}
                                </td>
                                <td className="font-mono text-sm">
                                  {user.email}
                                </td>
                                <td>
                                  <span
                                    className={`badge badge-sm ${
                                      user.status === 'ACTIVE'
                                        ? 'badge-success'
                                        : 'badge-warning'
                                    }`}
                                  >
                                    {user.status}
                                  </span>
                                </td>
                                <td>{formatDate(user.created_at)}</td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Recent Businesses */}
                <div className="card bg-base-100 shadow-sm">
                  <div className="card-body">
                    <h3 className="card-title text-secondary text-lg font-semibold">
                      🏢 Recent Businesses (
                      {analyticsData.detailedData.recentBusinesses.length})
                    </h3>
                    <p className="text-base-content/70 mb-4 text-sm">
                      Businesses and agents created in the last 30 days
                    </p>
                    <div className="max-h-96 overflow-x-auto">
                      <table className="table-sm table">
                        <thead>
                          <tr>
                            <th>Business Name</th>
                            <th>Agent Name</th>
                            <th>Type</th>
                            <th>Owner</th>
                            <th>Status</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.detailedData.recentBusinesses.map(
                            (business) => (
                              <tr key={business._id}>
                                <td className="font-medium">
                                  {business.business_name || 'N/A'}
                                </td>
                                <td>{business.agent_name}</td>
                                <td>
                                  <span className="badge badge-sm badge-outline">
                                    {business.agent_type}
                                  </span>
                                </td>
                                <td>{business.owner_name}</td>
                                <td>
                                  <span
                                    className={`badge badge-sm ${
                                      business.completed_onboarding
                                        ? 'badge-success'
                                        : 'badge-warning'
                                    }`}
                                  >
                                    {business.completed_onboarding
                                      ? 'Complete'
                                      : 'Setup'}
                                  </span>
                                </td>
                                <td>{formatDate(business.created_at)}</td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Original Monthly Data Tables */}
          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Recent Users */}
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h3 className="card-title text-lg font-semibold">
                  Monthly User Growth
                </h3>
                <div className="overflow-x-auto">
                  <table className="table-sm table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>New Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.monthlyStats.users
                        .slice(-6)
                        .map((item) => (
                          <tr key={item._id}>
                            <td>{item._id}</td>
                            <td>{item.count}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h3 className="card-title text-lg font-semibold">
                  Monthly Agent Creation
                </h3>
                <div className="overflow-x-auto">
                  <table className="table-sm table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>New Agents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.monthlyStats.agents
                        .slice(-6)
                        .map((item) => (
                          <tr key={item._id}>
                            <td>{item._id}</td>
                            <td>{item.count}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Management Section */}
      {activeSection === 'content' && (
        <div className="space-y-6">
          {/* Content Filters */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <div className="mb-4 flex items-center gap-4">
                <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
                  <span className="text-primary text-lg">🔍</span>
                </div>
                <div>
                  <h3 className="text-base-content font-semibold">
                    Filter Content
                  </h3>
                  <p className="text-base-content/60 text-sm">
                    Search and filter your content library
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Content Type</span>
                  </label>
                  <select
                    className="select select-bordered w-full"
                    value={contentFilter.type}
                    onChange={(e) =>
                      setContentFilter((prev) => ({
                        ...prev,
                        type: e.target.value,
                      }))
                    }
                  >
                    <option value="">📄 All Types</option>
                    <option value="blog">📝 Blog Posts</option>
                    <option value="document">📋 Documents</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">
                      Publication Status
                    </span>
                  </label>
                  <select
                    className="select select-bordered w-full"
                    value={contentFilter.status}
                    onChange={(e) =>
                      setContentFilter((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="">🎯 All Statuses</option>
                    <option value="draft">📝 Draft</option>
                    <option value="published">✅ Published</option>
                    <option value="archived">📦 Archived</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Search</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="input input-bordered w-full pr-10"
                      placeholder="Search content..."
                      value={contentFilter.searchTerm}
                      onChange={(e) =>
                        setContentFilter((prev) => ({
                          ...prev,
                          searchTerm: e.target.value,
                        }))
                      }
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg
                        className="text-base-content/60 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content List */}
          {contentLoading ? (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <div className="flex h-64 items-center justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                    <p className="text-base-content/60">Loading content...</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <div className="mb-6 flex items-center gap-4">
                  <div className="bg-secondary/10 flex h-10 w-10 items-center justify-center rounded-full">
                    <span className="text-secondary text-lg">📋</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base-content font-semibold">
                      Content Library
                    </h3>
                    <p className="text-base-content/60 text-sm">
                      {contentData?.list_content?.data?.contents?.length || 0}{' '}
                      items found
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => refetchContent()}
                      className="btn btn-ghost btn-sm"
                      disabled={contentLoading}
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {!contentData?.list_content?.data?.contents ||
                contentData.list_content.data.contents.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="bg-base-200 mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full">
                      <span className="text-4xl">📝</span>
                    </div>
                    <h3 className="text-base-content mb-2 text-lg font-semibold">
                      No content found
                    </h3>
                    <p className="text-base-content/60 mb-6">
                      Get started by creating your first content item
                    </p>
                    <button
                      onClick={handleCreateContent}
                      className="btn btn-primary"
                      disabled={creating}
                    >
                      <svg
                        className="mr-2 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Create Content
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table-zebra table">
                      <thead>
                        <tr>
                          <th className="w-1/3">Title</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Author</th>
                          <th>Modified</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contentData?.list_content?.data?.contents?.map(
                          (content: Content) => (
                            <tr key={content._id} className="hover">
                              <td>
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    <span className="text-base-content font-medium">
                                      {content.metadata.title.en ||
                                        content.metadata.title.es ||
                                        'Untitled'}
                                    </span>
                                    {content.metadata.featured && (
                                      <div
                                        className="tooltip"
                                        data-tip="Featured content"
                                      >
                                        <span className="text-yellow-500">
                                          ⭐
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {(content.metadata?.description?.en ||
                                    content.metadata?.description?.es) && (
                                    <span className="text-base-content/60 mt-1 line-clamp-1 text-sm">
                                      {content.metadata?.description?.en ||
                                        content.metadata?.description?.es}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="badge badge-outline gap-1">
                                  <span>
                                    {content.type === 'blog' ? '📝' : '📋'}
                                  </span>
                                  {content.type === 'blog'
                                    ? 'Blog'
                                    : 'Document'}
                                </div>
                              </td>
                              <td>
                                <div
                                  className={`badge ${
                                    content.status === 'published'
                                      ? 'badge-success'
                                      : content.status === 'draft'
                                        ? 'badge-warning'
                                        : 'badge-error'
                                  }`}
                                >
                                  <span className="mr-1">
                                    {content.status === 'published'
                                      ? '✅'
                                      : content.status === 'draft'
                                        ? '📝'
                                        : '📦'}
                                  </span>
                                  {content.status.charAt(0).toUpperCase() +
                                    content.status.slice(1)}
                                </div>
                              </td>
                              <td>
                                <div className="flex items-center gap-2">
                                  <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full">
                                    <span className="text-primary text-xs font-medium">
                                      {(content.metadata.author || 'U')
                                        .charAt(0)
                                        .toUpperCase()}
                                    </span>
                                  </div>
                                  <span className="text-sm">
                                    {content.metadata.author || 'Unknown'}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="text-base-content/60 text-sm">
                                  {formatDate(content.metadata.dateModified)}
                                </div>
                              </td>
                              <td>
                                <div className="flex justify-end gap-1">
                                  <div
                                    className="tooltip"
                                    data-tip="Edit content"
                                  >
                                    <button
                                      onClick={() => handleEditContent(content)}
                                      className="btn btn-ghost btn-sm btn-square"
                                      disabled={updating}
                                    >
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                  <div
                                    className="tooltip"
                                    data-tip="Delete content"
                                  >
                                    <button
                                      onClick={() =>
                                        handleDeleteContent(content._id)
                                      }
                                      className="btn btn-ghost btn-sm btn-square text-error hover:bg-error/10"
                                      disabled={deleting}
                                    >
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Referrals Section */}
      {activeSection === 'referrals' && <ReferralsDashboard />}

      {/* Content Editor Dialog */}
      <ContentEditorDialog
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        content={selectedContent}
        onSave={handleSaveContent}
        isNew={!selectedContent}
        isSaving={creating || updating}
      />
    </div>
  );
};

export default AdminAnalytics;
